import { useEffect, useState } from "react";
import {
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  CheckOutlined as CheckOutlinedIcon,
  CloseOutlined as CloseOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  ModeEditOutlineOutlined as ModeEditOutlineOutlinedIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  SecurityOutlined as SecurityOutlinedIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useLayoutMainContext } from "src/Contexts/MainLayoutContext";
import { CardCore } from "src/Pages/components/CardCore/CardCore";
import { FormatDateBR } from "src/shared/Utils/FormatDateBR";
import { api } from "src/shared/setup/API/api";
import { useProducerInternalData } from "../../hooks/useProducerInternalData";
import { DefineProducerPasswordModal } from "./DefineProducerPasswordModal";

export const TabAcessoOnline = () => {
  const { producerListItem, producerForm, producerId } = useProducerInternalData();
  const { handleGetAlert } = useLayoutMainContext();
  const [loading, setLoading] = useState(false);
  const [isActiveAccess, setIsActiveAccess] = useState(
    Boolean(producerListItem?.is_active_access_producer)
  );
  const [emailAccess, setEmailAccess] = useState(
    producerForm.email || producerListItem?.email || producerForm.email_contato || ""
  );
  const [initialEmailAccess, setInitialEmailAccess] = useState(
    producerForm.email || producerListItem?.email || producerForm.email_contato || ""
  );
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [openModalDefinePassword, setOpenModalDefinePassword] = useState(false);

  useEffect(() => {
    const initialEmail =
      producerForm.email || producerListItem?.email || producerForm.email_contato || "";
    setIsActiveAccess(Boolean(producerListItem?.is_active_access_producer));
    setEmailAccess(initialEmail);
    setInitialEmailAccess(initialEmail);
    setIsEditingEmail(false);
  }, [
    producerListItem?.is_active_access_producer,
    producerListItem?.email,
    producerForm.email,
    producerForm.email_contato,
  ]);

  const handleChangeAccessStatus = async (nextValue: boolean) => {
    const previousValue = isActiveAccess;
    setIsActiveAccess(nextValue);
    setLoading(true);

    await api
      .patch(`/produtor/changeIsActiveAccess/${producerId}`, {
        is_active_access: nextValue,
      })
      .then((res) => handleGetAlert({ message: res.data.message }))
      .catch((error) => {
        setIsActiveAccess(previousValue);
        handleGetAlert({ message: error?.response?.data?.message || "Erro ao alterar acesso" });
      })
      .finally(() => setLoading(false));
  };

  const handleSaveEmailAccess = async () => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAccess.trim());
    if (!emailAccess.trim()) {
      handleGetAlert({ message: "Informe o e-mail de acesso" });
      return;
    }
    if (!isValidEmail) {
      handleGetAlert({ message: "Digite um e-mail valido" });
      return;
    }

    setLoading(true);
    await api
      .patch(`/produtor/changeEmail/${producerId}`, {
        email: emailAccess || "",
      })
      .then((res) => {
        handleGetAlert({ message: res.data.message });
        setInitialEmailAccess(emailAccess);
        setIsEditingEmail(false);
      })
      .catch((error) =>
        handleGetAlert({ message: error?.response?.data?.message || "Erro ao salvar e-mail" })
      )
      .finally(() => setLoading(false));
  };

  const handleCancelEditEmail = () => {
    setEmailAccess(initialEmailAccess);
    setIsEditingEmail(false);
  };

  const handleSendNewPasswordByEmail = async () => {
    if (!emailAccess.trim()) {
      handleGetAlert({ message: "Informe o e-mail de acesso para enviar nova senha" });
      return;
    }

    setLoading(true);
    await api
      .patch(`/produtor/changePassword/${producerId}`, {
        sendByEmail: true,
      })
      .then((res) => {
        handleGetAlert({
          message: res.data.message || "Nova senha enviada para o e-mail do produtor",
        });
      })
      .catch((error) =>
        handleGetAlert({
          message:
            error?.response?.data?.message || "Erro ao enviar nova senha por e-mail",
        })
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <DefineProducerPasswordModal
        open={openModalDefinePassword}
        setOpen={setOpenModalDefinePassword}
        producerId={producerId}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        <CardCore sxStyle={{ margin: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityOutlinedIcon sx={{ color: "#1f6fe5", fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Configuracao de Acesso
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>
                Gerencie o acesso online do produtor
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              border: "1px solid #e9edf3",
              borderRadius: "10px",
              p: 1.2,
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#fff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonOutlineOutlinedIcon sx={{ color: "#8a94a6", fontSize: 17 }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: ".92rem" }}>
                  Acesso Online
                </Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>
                  {isActiveAccess ? "Acesso ativado" : "Acesso desativado"}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={isActiveAccess}
              disabled={loading}
              onChange={(_, checked) => handleChangeAccessStatus(checked)}
            />
          </Box>

          <Typography sx={{ fontWeight: 600, fontSize: ".82rem", mt: 2, mb: 0.8 }}>
            Email de Acesso
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={emailAccess}
              onChange={(e) => setEmailAccess(e.target.value)}
              disabled={!isEditingEmail || loading}
              placeholder="email@exemplo.com"
              helperText={
                isEditingEmail
                  ? "Edite o e-mail e clique em Salvar para enviar nova credencial automaticamente."
                  : "Clique em Editar para alterar o e-mail de acesso do produtor."
              }
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "10px",
                  backgroundColor: isEditingEmail ? "#fff" : "#f7f8fb",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isEditingEmail ? (
                      <ModeEditOutlineOutlinedIcon sx={{ fontSize: 17, color: "#2563eb" }} />
                    ) : (
                      <LockOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              {isEditingEmail ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloseOutlinedIcon />}
                    onClick={handleCancelEditEmail}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CheckOutlinedIcon />}
                    onClick={handleSaveEmailAccess}
                    disabled={loading || emailAccess.trim() === initialEmailAccess.trim()}
                    sx={{ textTransform: "none" }}
                  >
                    Salvar
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ModeEditOutlineOutlinedIcon />}
                  onClick={() => setIsEditingEmail(true)}
                  disabled={loading}
                  sx={{ textTransform: "none" }}
                >
                  Editar e-mail
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 600, fontSize: ".82rem", mb: 1 }}>
            Gerenciamento de Senha
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<KeyOutlinedIcon />}
              onClick={() => setOpenModalDefinePassword(true)}
              disabled={loading}
              sx={{ justifyContent: "flex-start", textTransform: "none", borderRadius: "8px", padding: "12px" }}
            >
              Definir nova senha para o produtor
            </Button>
          </Stack>
        </CardCore>

        <CardCore sxStyle={{ margin: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeOutlinedIcon sx={{ color: "#1f6fe5", fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Informacoes de Login
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>
                Historico e dados de acesso
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1.2} sx={{ mt: 2 }}>
            <Box sx={{ border: "1px solid #e9edf3", borderRadius: "10px", p: 1.2 }}>
              <Typography sx={{ color: "text.secondary", fontSize: ".77rem" }}>Status</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: ".94rem", mt: 0.3 }}>
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: isActiveAccess ? "#16a34a" : "#9ca3af",
                    display: "inline-block",
                    mr: 0.8,
                  }}
                />
                {isActiveAccess ? "Ativo" : "Inativo"}
              </Typography>
            </Box>

            <Box sx={{ border: "1px solid #e9edf3", borderRadius: "10px", p: 1.2 }}>
              <Typography sx={{ color: "text.secondary", fontSize: ".77rem" }}>
                Email Cadastrado
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: ".94rem", mt: 0.3 }}>
                {emailAccess || "-"}
              </Typography>
            </Box>

            <Box sx={{ border: "1px solid #e9edf3", borderRadius: "10px", p: 1.2 }}>
              <Typography sx={{ color: "text.secondary", fontSize: ".77rem" }}>
                Telefone (WhatsApp)
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: ".94rem", mt: 0.3 }}>
                {producerForm.telefone1 || "-"}
              </Typography>
            </Box>

            <Box sx={{ border: "1px solid #e9edf3", borderRadius: "10px", p: 1.2 }}>
              <Typography sx={{ color: "text.secondary", fontSize: ".77rem" }}>
                Conta criada em
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: ".94rem", mt: 0.3 }}>
                {producerListItem?.created_at ? FormatDateBR(producerListItem.created_at) : "-"}
              </Typography>
            </Box>
          </Stack>
        </CardCore>
      </Box>
    </>
  );
};
